import MapComponent from '../../../ui/Map/Map';
import PublicRoute from 'enums/publicRoutes';
import { Button, Icon, Spinner } from '../../../ui';
import { StyledMap } from './style';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FilterOptions } from '../../../types/FilterOptions';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { Form } from '../../../ui/Form/Form';
import { FarmData } from '../../../types/FarmData';
import { FarmCard } from '../../../ui/Card/FarmCard';
import { useIntl } from 'react-intl';
import { Pagination } from 'antd';
import { Card } from '../../../ui/Card';
import { ProductData } from '../../../types/ProductData';
import Banner from '../../components/DefaultLayout/Banner';
import { useBannerState } from '../../context';
import { BannerProps } from '../../../types/BannerData';
import { paginationNavigationRenderer } from 'utils/paginationNavigationrenderer';
import DefaultLayout from 'public/components/DefaultLayout';
import useMarketType from '../../../utils/useMarketType';
import { useBreakpoint } from 'utils/useBreakpoint';
import useSearchParamsHelper from '../../../utils/useSearchParamsHelper';
import {
  DEFAULT_MAP_DATA_TYPE,
  MapDataType,
  buildListNavigationParams,
  buildFilterChangeParams,
  buildMapNavigationParams,
  addFilterEntriesToParams,
  clearFilterValues,
  searchParamsEqual,
} from '../../../utils/filterHelper';
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  buildPath,
  readPagination,
  writePagination,
} from '../../../utils/listPagination';
import { Filter } from '../../../types/Filter';
import useScroll from 'utils/useScroll';
import { options } from 'config/config';
import { useClearFilterHandlers } from '../../../utils/useClearFilterHandlers';

const DEFAULT_IMAGE = options.images.defaultProductImage;

const MAP_FILTER_FIELDS = [
  'qmark',
  'delivery',
  'emarket',
  'services',
  'farm',
  'cooperative',
];

interface MapPageCoordinate {
  farmdata?: {
    farm_id?: number | null;
    id?: number | null;
    coordinates?: string | null;
    product_id?: number | null;
  };
  farm_id?: number | null;
  logo?: string;
  name?: string;
  actual_full_address?: string;
}

const MapPage = () => {
  const location = useLocation();
  const mapLocationState = location.state;
  const urlParams = new URLSearchParams(location.search);
  const {
    dataType: stateDataType,
    page: initialStatePage,
    page_size: initialStatePageSize,
    total: initialStateTotal,
  } = mapLocationState || {};
  const dataType = (urlParams.get('dataType') ||
    stateDataType ||
    DEFAULT_MAP_DATA_TYPE) as MapDataType;
  const [currentFilters, setCurrentFilters] = useState<Filter>({});
  const [data, setData] = useState<(ProductData | FarmData)[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({});
  const [coordinates, setCoordinates] = useState<MapPageCoordinate[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [totalData, setTotalData] = useState(initialStateTotal || 0);
  const [prevSearch, setPrevSearch] = useState<string>('');
  const navigate = useNavigate();
  const { scrollToTop } = useScroll();

  const intl = useIntl();
  const { banners } = useBannerState();
  const { marketType, isEmarket } = useMarketType();
  const { isLgDown } = useBreakpoint();
  const { getProperFilterValues } = useSearchParamsHelper();

  const { page: currentPage, pageSize } = readPagination(urlParams);

  const banner_3_data = banners.data.find(
    (banner: BannerProps) => banner.type === 'banner_3'
  );
  const banner_4_data = banners.data.find(
    (banner: BannerProps) => banner.type === 'banner_4'
  );

  const { appendData, isLoading: isMapLoading } = useQueryApiClient({
    request: {
      url: `api/v2/${dataType}`,
      method: 'GET',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      let coords: MapPageCoordinate[] = [];
      if (dataType === 'products') {
        coords = (response?.farmsCoordinates || []).map(
          (farmCoord: MapPageCoordinate) => ({
            ...farmCoord,
            farmdata: {
              ...farmCoord.farmdata,
              product_id: farmCoord?.farmdata?.id ?? null,
            },
          })
        );
      } else {
        coords = response?.farmsCoordinates || [];
      }
      setCoordinates(coords);
      setFilterOptions(response.filters || {});
      setData(response[dataType].data);
      setTotalData(response[dataType].total);
    },
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const stateFilters = mapLocationState?.filters;

    if (!urlParams.get('dataType')) {
      urlParams.set('dataType', dataType);
    }
    if (!urlParams.has('page') && !urlParams.has('page_size')) {
      writePagination(urlParams, {
        page: initialStatePage || DEFAULT_PAGE,
        pageSize: initialStatePageSize || DEFAULT_PAGE_SIZE,
      });
    }

    const hasUrlFilters = MAP_FILTER_FIELDS.some((key) => urlParams.get(key));
    const hasEffectiveStateFilters =
      stateFilters &&
      Object.entries(stateFilters).some(
        ([, v]) =>
          v != null && v !== '' && !(Array.isArray(v) && v.length === 0)
      );
    if (
      (stateDataType === 'products' || stateDataType === 'farms') &&
      hasEffectiveStateFilters &&
      !hasUrlFilters
    ) {
      addFilterEntriesToParams(urlParams, stateFilters);
    }

    const currentSearch = urlParams.get('search') || '';
    const searchChanged = prevSearch !== currentSearch;
    const { page, pageSize: page_size } = readPagination(urlParams);

    if (searchChanged && page !== DEFAULT_PAGE) {
      writePagination(urlParams, { page: DEFAULT_PAGE, pageSize: page_size });
    }

    if (!searchParamsEqual(new URLSearchParams(location.search), urlParams)) {
      navigate(buildPath(location.pathname, urlParams), {
        replace: true,
        state: { ...location.state, dataType },
      });
      if (searchChanged) {
        setPrevSearch(currentSearch);
      }
      return;
    }

    const queryParams = getProperFilterValues([...MAP_FILTER_FIELDS]);

    const filterParams = { ...queryParams };
    delete filterParams.dataType;
    delete filterParams.page;
    delete filterParams.page_size;
    delete filterParams.search;

    setCurrentFilters(filterParams);

    appendData({
      filter: { ...filterParams, type: marketType },
      page,
      page_size,
      fullSearch: currentSearch,
    });

    setPrevSearch(currentSearch);
    scrollToTop();
  }, [location.pathname, location.search]);

  const handleFilterChange = (newFilter: Filter) => {
    const clearedFilter = clearFilterValues(newFilter);
    const oldParams = new URLSearchParams(location.search);
    const params = buildFilterChangeParams({
      filter: newFilter,
      search: oldParams.get('search') || undefined,
      pageSize,
    });
    params.set('dataType', dataType);

    navigate(buildPath(location.pathname, params), {
      replace: true,
      state: { ...location.state, dataType, filters: clearedFilter },
    });

    setCurrentFilters(clearedFilter);

    const hasLocation = !!clearedFilter?.location_radius;
    const hasDistance = !!clearedFilter?.distance_radius;

    if ((hasLocation && !hasDistance) || (!hasLocation && hasDistance)) {
      return;
    }
  };

  const { clearAppliedFilters } = useClearFilterHandlers({
    filter: currentFilters,
    onFilterChange: handleFilterChange,
  });

  const navigateBackToList = (replace = false) => {
    const path =
      dataType === 'products'
        ? mapLocationState?.returnPath ||
          (isEmarket
            ? PublicRoute.PRODUCTS_ALL
            : PublicRoute.WHOLESALE_PRODUCTS_ALL)
        : dataType === 'farms'
          ? isEmarket
            ? PublicRoute.FARMS_ALL
            : PublicRoute.WHOLESALE_FARMS_ALL
          : null;
    if (!path) {
      navigate(-1);
      return;
    }
    const params = buildListNavigationParams({
      page: currentPage,
      pageSize,
      filter: currentFilters,
      search: urlParams.get('search') || undefined,
    });
    navigate(`${path}?${params.toString()}`, { replace });
  };

  const handleOnClickBackButton = () => navigateBackToList();

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  const handleOpenFilter = () => {
    setIsFilterOpen(true);
  };

  const renderCards = () => {
    switch (dataType) {
      case 'products':
        return (data as ProductData[]).map((product, index: number) => (
          <Card
            product={product}
            imageUrl={
              product?.image_small
                ? product?.image_small
                : product?.farm?.logo
                  ? product?.farm?.logo
                  : DEFAULT_IMAGE
            }
            key={index}
            hoverable
          />
        ));
      case 'farms':
        return (data as FarmData[]).map((farm, index: number) => (
          <FarmCard farm={farm} isFarmList={true} key={index} hoverable />
        ));
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (isLgDown && (dataType === 'products' || dataType === 'farms')) {
        navigateBackToList(true);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navigate, dataType, location.state, isEmarket]);

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageContent className={'no-padding'}>
        <StyledMap>
          <div className={'wrapper'}>
            {isFilterOpen && (
              <Form
                setFilter={handleFilterChange}
                filterOptions={filterOptions}
                filter={currentFilters}
                mapView
                onCloseFilter={handleFilterClose}
                onClearFilters={clearAppliedFilters}
              />
            )}
            <Spinner spinning={isMapLoading}>
              <div className="map-wrap">
                <MapComponent data={coordinates} dataType={dataType} />

                {!isFilterOpen && (
                  <div className={'on-map-btn-wrapper'}>
                    <Button
                      label={intl.formatMessage({
                        id: 'general.change_filter',
                      })}
                      icon={<Icon name={'search-icon-black'} />}
                      type={'primary'}
                      onClick={handleOpenFilter}
                      className={'on-map-btn'}
                    />
                  </div>
                )}
              </div>
            </Spinner>
          </div>
          <div className={'farms-list-wrapper container'}>
            <div className={'back-to-list-btn-wrapper'}>
              <div className={'back-to-list-btn'}>
                <Button
                  onClick={handleOnClickBackButton}
                  label={intl.formatMessage({ id: 'general.back_to_list' })}
                />
              </div>
            </div>
            <Spinner spinning={isMapLoading}>
              <div className={'farms-list'}>{renderCards()}</div>
            </Spinner>
            {totalData > 20 && (
              <Pagination
                total={totalData}
                showSizeChanger={false}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, newPageSize) => {
                  const params = buildMapNavigationParams({
                    dataType,
                    page,
                    pageSize: newPageSize,
                    filter: currentFilters,
                    search: urlParams.get('search') || undefined,
                  });
                  navigate(buildPath(location.pathname, params), {
                    replace: true,
                    state: { ...location.state, dataType },
                  });
                }}
                itemRender={paginationNavigationRenderer}
              />
            )}
          </div>
          <div className="banners">
            <div className="container">
              {banner_3_data && <Banner banners={[banner_3_data]} />}
              {banner_4_data && <Banner banners={[banner_4_data]} />}
            </div>
          </div>
        </StyledMap>
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default MapPage;
